import React, { useEffect, useMemo, useState } from "react";
import { Select } from "flowbite-react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import { HiCheck, HiOutlineCube, HiX } from "react-icons/hi";

import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAuth from "../../../../hooks/useAuth";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { useGetPasswordLevel } from "../../../../hooks/auth";
import {
  useDeleteEntityPreferencesByPath,
  useUpdateEntityPreferencesByPath,
} from "../../../../hooks/entity";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import UpgradeModal from "../../../modals/UpgradeModal";
import { PremiumBenefit } from "../../../atoms/UpgradePromptContent";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import WebhookIntegrationGuideModal from "./WebhookIntegrationGuide";
import WebhookSecretModal from "./WebhookSecretModal";
import { FieldLabel } from "../../../atoms/FieldLabel";

export type EntityPreferencesTypes = {
  data: any;
  user_identification: string;
  entity_identification: string;
};

type FormValues = {
  web_site_login_page: string;
  web_site_reset_password_page: string;
  cdn_logo_project_path: string;
  webhook_url: string;
  password_validation_regex_level: string;
  allowed_user_email_domain: string;
};

export const schema = Joi.object({
  web_site_login_page: Joi.string()
    .trim()
    .uri()
    .required()
    .label("Login page address"),
  web_site_reset_password_page: Joi.string()
    .trim()
    .uri()
    .required()
    .label("Reset password page address"),
  cdn_logo_project_path: Joi.string().trim().required().label("Logo path"),
  webhook_url: Joi.string()
    .trim()
    .uri()
    .optional()
    .allow(null, "")
    .label("Webhook URL"),
  password_validation_regex_level: Joi.string()
    .trim()
    .valid("level1", "level2", "level3", "level4", "level5", "level6")
    .required()
    .label("Login password security level"),
  allowed_user_email_domain: Joi.string()
    .trim()
    .optional()
    .allow(null, "")
    .label("Whitelist email domains"),
}).required();

const FIELD_ORDER: (keyof FormValues)[] = [
  "web_site_login_page",
  "web_site_reset_password_page",
  "webhook_url",
  "password_validation_regex_level",
  "allowed_user_email_domain",
  "cdn_logo_project_path",
];

const FIELD_LABEL_KEYS: Record<keyof FormValues, string> = {
  web_site_login_page: "dashboard.entities.entityPreferences.label-01",
  web_site_reset_password_page: "dashboard.entities.entityPreferences.label-02",
  webhook_url: "dashboard.entities.entityPreferences.label-webhook",
  password_validation_regex_level:
    "dashboard.entities.entityPreferences.label-04",
  allowed_user_email_domain: "dashboard.entities.entityPreferences.label-05",
  cdn_logo_project_path: "dashboard.develop.serviceAvatarUpdate.logo",
};

const toFormValues = (data: any): FormValues => ({
  web_site_login_page: data?.web_site_login_page ?? "",
  web_site_reset_password_page: data?.web_site_reset_password_page ?? "",
  cdn_logo_project_path: data?.cdn_logo_project_path ?? "",
  webhook_url: data?.webhook_url ?? "",
  password_validation_regex_level: data?.password_validation_regex_level ?? "",
  allowed_user_email_domain: Array.isArray(data?.allowed_user_email_domain)
    ? data.allowed_user_email_domain.join("\n")
    : data?.allowed_user_email_domain ?? "",
});

const EntityPreferences = ({
  entity_identification,
  user_identification,
  data,
}: EntityPreferencesTypes) => {
  const { t } = useTranslation();
  const { userEndedSubscription, user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canWriteEntitySettings = permissions.can(
    Cap.ENTITY_SETTINGS,
    Level.WRITE
  );
  const [showWebhookUpgradeModal, setShowWebhookUpgradeModal] = useState(false);
  const [showEmailDomainsUpgradeModal, setShowEmailDomainsUpgradeModal] =
    useState(false);
  const [showWebhookGuideModal, setShowWebhookGuideModal] = useState(false);
  const [showWebhookSecretModal, setShowWebhookSecretModal] = useState(false);

  const webhookBenefits: PremiumBenefit[] = useMemo(
    () => [
      {
        id: "webhook-real-time",
        text: t("dashboard.upgrade.webhook.benefit1") as string,
      },
      {
        id: "webhook-easy-setup",
        text: t("dashboard.upgrade.webhook.benefit2") as string,
      },
    ],
    [t]
  );

  const emailDomainsBenefits: PremiumBenefit[] = useMemo(
    () => [
      {
        id: "email-domains-security",
        text: t("dashboard.upgrade.emailDomains.benefit1") as string,
      },
      {
        id: "email-domains-compliance-control",
        text: t("dashboard.upgrade.emailDomains.benefit2") as string,
      },
      {
        id: "email-domains-blocking",
        text: t("dashboard.upgrade.emailDomains.benefit3") as string,
      },
    ],
    [t]
  );

  const {
    register,
    watch,
    reset,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty, dirtyFields },
  } = useForm<FormValues>({
    resolver: joiResolver(schema),
    mode: "onBlur",
    defaultValues: toFormValues(data),
  });

  const {
    data: passwordLevelsObj,
    isSuccess: isPassLevelsSuccess,
    isError: isPassLevelsError,
    refetch: passLevelRefetch,
  } = useGetPasswordLevel();

  useEffect(() => {
    reset(toFormValues(data));
  }, [data, reset]);

  const { mutateAsync: updateByPath, isLoading: isUpdating } =
    useUpdateEntityPreferencesByPath();
  const { mutateAsync: deleteByPath, isLoading: isDeleting } =
    useDeleteEntityPreferencesByPath();
  const isLoading = isUpdating || isDeleting;

  const valueToWrite = (field: keyof FormValues, values: FormValues) => {
    if (field === "allowed_user_email_domain") {
      return values.allowed_user_email_domain &&
        values.allowed_user_email_domain !== ""
        ? values.allowed_user_email_domain.split("\n")
        : [];
    }
    return values[field];
  };

  const onUpdatePreferences = async (values: FormValues) => {
    const pending = FIELD_ORDER.filter((field) => !!dirtyFields[field]);
    if (pending.length === 0) return;

    const saved: (keyof FormValues)[] = [];

    for (const field of pending) {
      try {
        if (
          field === "webhook_url" &&
          (!values.webhook_url || values.webhook_url === "")
        ) {
          await deleteByPath({
            user_identification,
            entity_identification,
            path: "webhook_url",
          });
        } else {
          await updateByPath({
            user_identification,
            entity_identification,
            path: field,
            value: valueToWrite(field, values),
          });
        }
        saved.push(field);
      } catch {
        break;
      }
    }

    const unsaved = pending.filter((field) => !saved.includes(field));

    if (unsaved.length === 0) {
      toast.success(t("dashboard.entities.entityPreferences.success"));
      return;
    }

    if (saved.length === 0) return;

    toast.error(
      <div className="text-sm text-gray-700">
        <p className="font-medium text-gray-900">
          {t("dashboard.entities.entityPreferences.success")}
        </p>
        <ul className="mt-1 space-y-0.5">
          {saved.map((field) => (
            <li key={field} className="flex items-start gap-1.5">
              <HiCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              <span>{t(FIELD_LABEL_KEYS[field])}</span>
            </li>
          ))}
        </ul>
        <ul className="mt-2 space-y-0.5 border-t border-gray-200 pt-2">
          {unsaved.map((field) => (
            <li key={field} className="flex items-start gap-1.5">
              <HiX className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <span>{t(FIELD_LABEL_KEYS[field])}</span>
            </li>
          ))}
        </ul>
      </div>,
      { autoClose: false }
    );
  };

  let content;

  if (isPassLevelsError)
    content = <CustomButton onClick={() => passLevelRefetch()} title="Retry" />;

  if (isPassLevelsSuccess)
    content = (
      <form onSubmit={handleSubmit(onUpdatePreferences)} id="linksConfig">
        <fieldset
          disabled={!canWriteEntitySettings}
          className="border-0 m-0 p-0 min-w-0"
        >
          <div className="pb-2 border-b border-solid">
            <div className="mb-3 w-full">
              <CustomInput
                name="web_site_login_page"
                control={control}
                error={errors.web_site_login_page}
                helperText={errors.web_site_login_page?.message}
                grayBackground={true}
                type="url"
                label={
                  t("dashboard.entities.entityPreferences.label-01") as string
                }
                maxLength={300}
              />
              <CharacterCounter
                currentLength={watch("web_site_login_page")?.length || 0}
                maxLength={300}
              />
            </div>

            <div className="mb-3 w-full">
              <CustomInput
                name="web_site_reset_password_page"
                control={control}
                error={errors.web_site_reset_password_page}
                helperText={errors.web_site_reset_password_page?.message}
                grayBackground={true}
                type="url"
                label={
                  t("dashboard.entities.entityPreferences.label-02") as string
                }
                maxLength={300}
              />
              <CharacterCounter
                currentLength={
                  watch("web_site_reset_password_page")?.length || 0
                }
                maxLength={300}
              />
            </div>

            <div className="mb-3 w-full">
              <div
                onClick={() => {
                  if (!permissions.isAtLeastTier(PlanTier.ENTERPRISE)) {
                    setShowWebhookUpgradeModal(true);
                  }
                }}
                className={
                  !permissions.isAtLeastTier(PlanTier.ENTERPRISE)
                    ? "cursor-pointer"
                    : ""
                }
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FieldLabel className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      {t("dashboard.entities.entityPreferences.label-webhook")}
                    </FieldLabel>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowWebhookGuideModal(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 underline underline-offset-2 font-normal whitespace-nowrap"
                    >
                      {t(
                        "dashboard.entities.entityPreferences.webhookGuideLink"
                      )}
                    </button>
                    {!permissions.isAtLeastTier(PlanTier.ENTERPRISE) && (
                      <UnlockBadge />
                    )}
                  </div>
                  {permissions.isAtLeastTier(PlanTier.ENTERPRISE) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowWebhookSecretModal(true);
                      }}
                      className="mt-1 sm:mt-0 shrink-0 self-start sm:self-auto inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2 whitespace-nowrap"
                    >
                      {t(
                        "dashboard.entities.entityPreferences.webhookSecret.manageLink"
                      )}
                    </button>
                  )}
                </div>
                <CustomInput
                  name="webhook_url"
                  control={control}
                  error={errors.webhook_url}
                  helperText={errors.webhook_url?.message}
                  grayBackground={true}
                  type="url"
                  disabled={!permissions.isAtLeastTier(PlanTier.ENTERPRISE)}
                  maxLength={300}
                />
                <CharacterCounter
                  currentLength={watch("webhook_url")?.length || 0}
                  maxLength={300}
                />
              </div>
            </div>

            <div className="mb-3 w-full">
              <FieldLabel
                htmlFor="password_validation_regex_level"
                className="text-sm font-medium text-gray-700"
              >
                {t("dashboard.entities.entityPreferences.label-04")}
              </FieldLabel>
              <Select
                translate="no"
                id="password_validation_regex_level"
                color={
                  !!errors.password_validation_regex_level
                    ? "failure"
                    : undefined
                }
                {...register("password_validation_regex_level")}
                helperText={
                  errors.password_validation_regex_level?.message as string
                }
              >
                <option value="">
                  {t("dashboard.entities.entityPreferences.select-option")}
                </option>
                {isPassLevelsSuccess &&
                  Object.keys(passwordLevelsObj).map((key) => (
                    <option key={key} value={key}>
                      {key} = {passwordLevelsObj[key]?.description}
                    </option>
                  ))}
              </Select>
            </div>

            <div className="my-5 w-full">
              <div
                onClick={() => {
                  if (!permissions.isAtLeastTier(PlanTier.ENTERPRISE)) {
                    setShowEmailDomainsUpgradeModal(true);
                  }
                }}
                className={
                  !permissions.isAtLeastTier(PlanTier.ENTERPRISE)
                    ? "cursor-pointer"
                    : ""
                }
              >
                <div className="flex items-center gap-2 mb-2">
                  <FieldLabel className="text-sm font-medium text-gray-700">
                    {t("dashboard.entities.entityPreferences.label-05")}
                  </FieldLabel>
                  {!permissions.isAtLeastTier(PlanTier.ENTERPRISE) && (
                    <UnlockBadge className="ml-2" />
                  )}
                </div>
                <CustomInput
                  rows={10}
                  name="allowed_user_email_domain"
                  control={control}
                  helperText={errors.allowed_user_email_domain?.message}
                  placeholder={
                    t(
                      "dashboard.entities.entityPreferences.label-05-placeholder"
                    ) as string
                  }
                  disabled={!permissions.isAtLeastTier(PlanTier.ENTERPRISE)}
                  maxLength={300}
                />
                <CharacterCounter
                  currentLength={
                    watch("allowed_user_email_domain")?.length || 0
                  }
                  maxLength={300}
                />
              </div>
            </div>
            {permissions.isAtLeastTier(PlanTier.ENTERPRISE) && (
              <p className="mt-1 text-xs text-gray-500">
                {t("dashboard.entities.entityPreferences.label-05-helper-01")}
                <br />
                {t("dashboard.entities.entityPreferences.label-05-helper-02")}
              </p>
            )}

            <CustomInput
              name="cdn_logo_project_path"
              control={control}
              type="hidden"
            />
          </div>

          {permissions.can(Cap.ENTITY_SETTINGS, Level.WRITE) && (
            <CustomButton
              type="submit"
              variant="primary"
              className="mt-5 mr-0 ml-auto"
              disabled={
                isLoading || userEndedSubscription || !isValid || !isDirty
              }
              isLoading={isLoading}
              title={t("common.update")}
            />
          )}
        </fieldset>
      </form>
    );

  return (
    <div>
      {content}
      <UpgradeModal
        showModal={showWebhookUpgradeModal}
        setShowModal={setShowWebhookUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.webhook.title") as string}
        description={t("dashboard.upgrade.webhook.description") as string}
        benefits={webhookBenefits}
        icon={HiOutlineCube}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
      <UpgradeModal
        showModal={showEmailDomainsUpgradeModal}
        setShowModal={setShowEmailDomainsUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.emailDomains.title") as string}
        description={t("dashboard.upgrade.emailDomains.description") as string}
        benefits={emailDomainsBenefits}
        icon={HiOutlineCube}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
      <WebhookIntegrationGuideModal
        show={showWebhookGuideModal}
        onClose={() => setShowWebhookGuideModal(false)}
      />
      <WebhookSecretModal
        show={showWebhookSecretModal}
        onClose={() => setShowWebhookSecretModal(false)}
        customer_identification={customer_identification}
        entity_identification={entity_identification}
      />
    </div>
  );
};

export default EntityPreferences;
